import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Flex, Result, Spin, Tag, Typography, theme } from '@/shared/antd-imports';
import { CreditCardOutlined, LockOutlined } from '@ant-design/icons';
import { formatCalendarDate, formatMinor } from '@/utils/money';
import type { PublicPayPage as PublicPayPageData } from '@/types/payment-schedule';

/** Backend origin: the portal API base without its `/api/client-portal` suffix. */
const backendOrigin = (): string =>
  (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/client-portal').replace(/\/api\/client-portal\/?$/, '');

interface IPublicResponse<T> {
  done: boolean;
  body: T & { code?: string };
  message?: string;
}

async function call<T>(path: string, init?: RequestInit): Promise<{ status: number; json: IPublicResponse<T> | null }> {
  const response = await fetch(`${backendOrigin()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  });
  let json: IPublicResponse<T> | null = null;
  try {
    json = (await response.json()) as IPublicResponse<T>;
  } catch {
    json = null;
  }
  return { status: response.status, json };
}

const POLL_MS = 3000;
const POLL_LIMIT = 40;

/**
 * Public pay page `/pay/:token` (ANS-45): the client sees what is due and pays it on the provider's hosted checkout,
 * without logging in. Coming back from the checkout (`?status=return`) only shows "processing" and checks again
 * every few seconds: the payment is recorded when the provider confirms it to the server, never by this page.
 */
const PublicPayPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const returned = params.get('status') === 'return';
  const cancelled = params.get('status') === 'cancelled';
  const { t, i18n } = useTranslation();
  const { token: design } = theme.useToken();
  const [page, setPage] = useState<PublicPayPageData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'error'>('loading');
  const [starting, setStarting] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const polls = useRef(0);

  const load = useCallback(
    async (quiet = false) => {
      if (!token) return;
      if (!quiet) setState('loading');
      try {
        const { status, json } = await call<PublicPayPageData>(`/public/pay/${encodeURIComponent(token)}`);
        if (status === 404) return setState('invalid');
        if (!json?.done) return quiet ? undefined : setState('error');
        setPage(json.body);
        setState('ready');
      } catch {
        if (!quiet) setState('error');
      }
    },
    [token]
  );

  useEffect(() => {
    void load();
  }, [load]);

  // After the checkout: check again until the payment is confirmed (or give up quietly).
  const waiting = returned && page !== null && page.status !== 'paid' && page.status !== 'cancelled';
  useEffect(() => {
    if (!waiting || polls.current >= POLL_LIMIT) return undefined;
    const timer = window.setTimeout(() => {
      polls.current += 1;
      void load(true);
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [waiting, page, load]);

  const pay = async () => {
    if (!token) return;
    setStarting(true);
    setPayError(null);
    try {
      const { json } = await call<{ url: string }>(`/public/pay/${encodeURIComponent(token)}/checkout`, { method: 'POST', body: '{}' });
      if (json?.done && json.body?.url) {
        window.location.assign(json.body.url);
        return;
      }
      setPayError(json?.message || t('payPage.startError', { defaultValue: 'Could not start the payment. Please try again.' }));
    } catch {
      setPayError(t('payPage.startError', { defaultValue: 'Could not start the payment. Please try again.' }));
    }
    setStarting(false);
  };

  const amount = (minor: number, currency: string) => formatMinor(minor, currency, i18n.language);

  return (
    <div style={{ minHeight: '100vh', background: design.colorBgLayout, padding: '24px 16px' }}>
      <Flex vertical gap={16} style={{ maxWidth: 560, margin: '0 auto' }}>
        {state === 'loading' && (
          <Flex justify="center" style={{ padding: 48 }}>
            <Spin size="large" />
          </Flex>
        )}
        {state === 'invalid' && (
          <Result
            status="warning"
            title={t('payPage.invalid', { defaultValue: 'This payment link is invalid or has expired' })}
            subTitle={t('payPage.invalidHelp', { defaultValue: 'Ask the person who sent it for a new link.' })}
          />
        )}
        {state === 'error' && (
          <Alert
            type="error"
            showIcon
            message={t('payPage.loadError', { defaultValue: 'Could not load this payment. Please try again.' })}
            action={
              <Button size="small" onClick={() => load()}>
                {t('payPage.retry', { defaultValue: 'Try again' })}
              </Button>
            }
          />
        )}
        {state === 'ready' && page && (
          <Card>
            <Flex vertical gap={16}>
              <Flex vertical gap={4}>
                <Typography.Text type="secondary">{page.team_name}</Typography.Text>
                <Typography.Title level={3} style={{ margin: 0, overflowWrap: 'anywhere' }}>
                  {page.project_name}
                </Typography.Title>
                <Typography.Text style={{ overflowWrap: 'anywhere' }}>
                  {page.milestone_name ? `${page.payment_name} · ${page.milestone_name}` : page.payment_name}
                </Typography.Text>
                {page.test_mode && (
                  <div>
                    <Tag color="gold">{t('payPage.testMode', { defaultValue: 'Test mode: no real money moves' })}</Tag>
                  </div>
                )}
              </Flex>

              {page.status === 'paid' && (
                <Result
                  status="success"
                  title={t('payPage.paidTitle', { defaultValue: 'Paid. Thank you!' })}
                  subTitle={
                    page.last_paid
                      ? t('payPage.paidOn', {
                          defaultValue: '{{amount}} received on {{date}}. A receipt was sent by email.',
                          amount: amount(page.last_paid.amount_minor, page.last_paid.currency),
                          date: new Date(page.last_paid.paid_at).toLocaleDateString(i18n.language),
                        })
                      : t('payPage.paidHelp', { defaultValue: 'This payment is settled. Nothing more is due.' })
                  }
                />
              )}

              {(page.status === 'processing' || (waiting && page.status === 'payable')) && (
                <Flex vertical align="center" gap={12} style={{ padding: '16px 0' }} role="status" aria-live="polite">
                  <Spin />
                  <Typography.Text strong>{t('payPage.processing', { defaultValue: 'We are confirming your payment…' })}</Typography.Text>
                  <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
                    {t('payPage.processingHelp', {
                      defaultValue: 'This can take a moment. You can close this page; you will get a receipt by email once it is confirmed.',
                    })}
                  </Typography.Text>
                </Flex>
              )}

              {page.status === 'cancelled' && (
                <Alert type="info" showIcon message={t('payPage.notPayable', { defaultValue: 'This payment cannot be paid online. Contact the sender.' })} />
              )}
              {page.status === 'unavailable' && (
                <Alert type="info" showIcon message={t('payPage.unavailable', { defaultValue: 'Online payment is not available for this payment right now. Contact the sender.' })} />
              )}

              {page.status === 'payable' && !waiting && (
                <Flex vertical gap={12}>
                  {cancelled && (
                    <Alert type="info" showIcon message={t('payPage.cancelled', { defaultValue: 'The payment was not completed. You can try again.' })} />
                  )}
                  <Flex justify="space-between" align="baseline" gap={12} wrap="wrap">
                    <Typography.Text type="secondary">{t('payPage.amountDue', { defaultValue: 'Amount due' })}</Typography.Text>
                    <Typography.Title level={2} style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {amount(page.amount_due_minor, page.currency)}
                    </Typography.Title>
                  </Flex>
                  {page.due_date && (
                    <Typography.Text type="secondary">
                      {t('payPage.dueOn', { defaultValue: 'Due on {{date}}', date: formatCalendarDate(page.due_date, i18n.language) })}
                    </Typography.Text>
                  )}
                  {payError && <Alert type="error" showIcon message={payError} />}
                  <Button type="primary" size="large" block icon={<CreditCardOutlined />} loading={starting} onClick={pay}>
                    {t('payPage.payButton', { defaultValue: 'Pay {{amount}}', amount: amount(page.amount_due_minor, page.currency) })}
                  </Button>
                  <Typography.Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
                    <LockOutlined style={{ marginInlineEnd: 4 }} />
                    {t('payPage.secure', { defaultValue: 'You pay on Stripe’s secure page. Your card details never reach us.' })}
                  </Typography.Text>
                </Flex>
              )}
            </Flex>
          </Card>
        )}
      </Flex>
    </div>
  );
};

export default PublicPayPage;
